import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Layout,
  Link,
  Page,
  Popover,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from "@shopify/polaris";
import { DeleteIcon, ImportIcon } from "@shopify/polaris-icons";
import { useLocation, useParams } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { formatPlate } from "../../utility/funcs";
import { UserAuth } from "../../context/AuthContext";
import SpeiPaymentModal from "../Services/SpeiPaymentModal";
import { speiEnabled } from "../../utility/pagos";
import {
  METHOD_LABEL,
  NO_INVOICE_LABEL,
  PAYEE_TYPE_LABEL,
  TRIGGER_LABEL,
  egresoStatus,
  money,
  openEgresoDoc,
  payeeUrl,
  rpcError,
} from "../../utility/egresos";
import { CostModal, MoneyModal, NoInvoiceModal, ReasonModal } from "./EgresoModals";
import {
  AccountCard,
  PendingSpeiCard,
  SpeiRequestModal,
  openSpeiReceipt,
  useEgresoAccount,
  usePendingSpei,
} from "./EgresoSpei";
import EditPayeeModal from "./EditPayeeModal";
import ReturnClabeCard from "./ReturnClabeCard";
import CategorySelect, { useEgresoCategories } from "./CategorySelect";
import CarPicker from "./CarPicker";

// One egreso: what was agreed, the invoices, the payments, and which invoice
// each payment covers. Everything is read from views; actions are RPCs.
// Deliberately quiet: no fiscal controls (deducibility is derived, never
// chosen) and a problem only shows where it is.

// A bare date ("2026-10-20", e.g. a planned payment) is read at local noon:
// parsed as UTC midnight it showed the day before in Mexico.
const fmtDate = (d) =>
  d
    ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00` : d).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })
    : "—";
// Older SPEI entries carried the tracking key in the concept; it belongs to the payout.
const cleanConcept = (s) => (s || "").replace(/\s*·?\s*SPEI\s+\S+$/, "").trim();
const folioOf = (c) => c?.serie_folio || c?.cfdi_uuid?.slice(0, 8);
const NOT_DEDUCTIBLE_SHOWN = /cancelado|EFOS|nombre de GoMove|efectivo/i;

export default function ViewEgreso() {
  const { egresoId } = useParams();
  const id = Number(egresoId);
  const { hasPermission, session } = UserAuth();
  const canPay = !!hasPermission?.("services.pay");
  // Staff manage invoices and payees; someone with only egresos.request may
  // just look at (and, while pending, edit) their own request.
  const canManage = !!hasPermission?.("services.create");
  const [requester, setRequester] = useState(null);

  const [eg, setEg] = useState(null);
  const [bal, setBal] = useState(null);
  const [payee, setPayee] = useState(null);
  const [costs, setCosts] = useState([]);
  const [moneyLines, setMoneyLines] = useState([]);
  const [allocs, setAllocs] = useState([]);
  const [plan, setPlan] = useState([]);
  const [repDocs, setRepDocs] = useState([]);
  const [client, setClient] = useState(null);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState(null);
  const [account, reloadAccount] = useEgresoAccount(id);
  const spei = usePendingSpei(id);
  // Problems attaching the document right after creating the egreso.
  const createdProblems = useLocation().state?.problems || [];

  const load = useCallback(async () => {
    const [e, b, p, c, m, a, pl] = await Promise.all([
      supabase.from("egresos").select("*, car:cars(id, plate, year, model:car_models(name))").eq("id", id).maybeSingle(),
      supabase.from("egreso_balances").select("*").eq("egreso_id", id).maybeSingle(),
      supabase.from("egreso_payees").select("*").eq("egreso_id", id).maybeSingle(),
      supabase.from("egreso_cost_lines").select("*").eq("egreso_id", id).order("occurred_at"),
      supabase.from("egreso_money_lines").select("*").eq("egreso_id", id).order("occurred_at"),
      supabase.from("egreso_allocations").select("*").eq("egreso_id", id),
      supabase.from("egreso_planned_payments").select("*").eq("egreso_id", id).order("position"),
    ]);
    setEg(e.data);
    setBal(b.data);
    setPayee(p.data);
    setCosts(c.data || []);
    setMoneyLines(m.data || []);
    setAllocs(a.data || []);
    setPlan(pl.data || []);
    // REP lines (synced from SAT) for this egreso's invoices.
    const uuids = (c.data || []).map((x) => x.cfdi_uuid).filter(Boolean);
    if (uuids.length) {
      const { data: rd } = await supabase
        .from("expense_rep_docs")
        .select("*")
        .in("invoice_uuid", uuids.map((u) => u.toUpperCase()))
        .order("fecha_pago");
      setRepDocs(rd || []);
    } else setRepDocs([]);
    if (e.data?.created_by && e.data.approval !== "aprobada") {
      const { data: u } = await supabase.from("users").select("name").eq("id", e.data.created_by).maybeSingle();
      setRequester(u?.name || null);
    } else setRequester(null);
    if (e.data?.customer_id) {
      const { data: cu } = await supabase.from("customers").select("id, first_name, last_name").eq("id", e.data.customer_id).maybeSingle();
      setClient(cu && { url: `/customers/edit?id=${cu.id}`, name: `${cu.first_name || ""} ${cu.last_name || ""}`.trim() });
    } else if (e.data?.company_id) {
      const { data: co } = await supabase.from("companies").select("id, name").eq("id", e.data.company_id).maybeSingle();
      setClient(co && { url: `/companies/edit?id=${co.id}`, name: co.name });
    } else setClient(null);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Net allocation per (payment, invoice): reversals cancel their originals.
  const coverage = useMemo(() => {
    const net = {};
    for (const a of allocs) {
      const k = `${a.money_entry_id}:${a.cost_entry_id}`;
      net[k] = net[k] || { money: a.money_entry_id, cost: a.cost_entry_id, cents: 0, rep: null, id: null };
      net[k].cents += Number(a.amount_cents);
      if (!a.reverses_id) {
        net[k].id = a.id;
        net[k].rep = a.rep_uuid;
      }
    }
    return Object.values(net).filter((x) => x.cents !== 0);
  }, [allocs]);
  const costById = useMemo(() => Object.fromEntries(costs.map((c) => [c.entry_id, c])), [costs]);
  const moneyById = useMemo(() => Object.fromEntries(moneyLines.map((m) => [m.entry_id, m])), [moneyLines]);

  if (!eg) {
    return (
      <AppWrapper>
        <Page title="Egreso">
          <div style={{ padding: 40, textAlign: "center" }}><Spinner /></div>
        </Page>
      </AppWrapper>
    );
  }

  const st = egresoStatus(bal, eg.closed_at, eg.approval);
  const closed = !!eg.closed_at;
  const isRequest = eg.approval === "pendiente" && !closed;
  const canEdit = canManage || (isRequest && eg.created_by === session?.user?.id);
  const refresh = () => {
    load();
    spei.reload();
    reloadAccount();
  };
  const done = () => {
    setModal(null);
    refresh();
  };
  // "Quitar": undo something recorded by mistake (an invoice attached to the
  // wrong egreso, a manual payment typed twice). Appends the reversal; a SPEI
  // can never be removed this way, only a bank return reverses it.
  const remove = (entryId, what) =>
    setModal({
      type: "reason",
      title: `Quitar ${what}`,
      label: "¿Por qué se quita?",
      primary: "Quitar",
      destructive: true,
      help:
        what === "factura"
          ? "Para corregir una factura que se agregó por error. La factura regresa a la bandeja de recibidas."
          : "Para corregir un pago que se registró por error. Queda en el historial.",
      onConfirm: async (note) => {
        const { error: err } = await supabase.rpc("reverse_egreso_entry", { p_entry: entryId, p_note: note });
        if (err) return rpcError(err);
        done();
      },
    });

  const allClear = bal && !bal.por_pagar_cents && !bal.por_comprobar_cents && !bal.deposito_cents;

  // Planned payments are covered in order by what has been paid so far.
  let paidLeft = Number(bal?.net_paid_cents || 0);
  const planRows = plan.map((p) => {
    const covered = Math.min(paidLeft, Number(p.amount_cents));
    paidLeft -= covered;
    return { ...p, covered };
  });
  const nextPlanned = planRows.find((p) => p.covered < p.amount_cents);

  // Primary action: pay by SPEI (or send the request already prepared).
  const speiReady = canPay && speiEnabled() && !closed && spei.request !== undefined;
  // Something to pay: what is still owed, or — with no agreed amount and no
  // invoice yet — money advanced before any document (an anticipo).
  const noAmountYet = !(eg.committed_cents > 0) && !((bal?.cost_cents || 0) > 0);
  const somethingToPay = noAmountYet || (bal?.por_pagar_cents || 0) > 0;
  const primaryAction = !speiReady
    ? undefined
    : spei.request
      ? spei.inFlight
        ? { content: "SPEI en curso", disabled: true }
        : { content: `Enviar SPEI ${money(spei.request.amount_cents)}`, onAction: () => setModal({ type: "send", requestId: spei.request.id }) }
      : somethingToPay
        ? { content: isRequest ? "Aprobar y pagar por SPEI" : "Pagar por SPEI", onAction: () => setModal({ type: "speiRequest" }) }
        : undefined;

  // A pending request is decided by paying it (above) or rejecting it.
  const rejectAction = {
    content: "Rechazar",
    destructive: true,
    onAction: () =>
      setModal({
        type: "reason",
        title: "Rechazar solicitud",
        label: "¿Por qué se rechaza?",
        primary: "Rechazar",
        destructive: true,
        onConfirm: async (reason) => {
          const { error: err } = await supabase.rpc("reject_egreso", { p_egreso: id, p_reason: reason });
          if (err) return rpcError(err);
          done();
        },
      }),
  };

  const closeAction = !canPay
    ? []
    : isRequest
      ? [rejectAction]
      : closed
        ? [{ content: "Reabrir", onAction: async () => { await supabase.rpc("reopen_egreso", { p_egreso: id }); load(); } }]
        : [
            {
              content: "Cerrar egreso",
              onAction: () =>
                allClear
                  ? supabase.rpc("close_egreso", { p_egreso: id }).then(({ error: err }) => (err ? setError(rpcError(err)) : load()))
                  : setModal({
                      type: "reason",
                      title: "Cerrar con saldos abiertos",
                      label: "¿Por qué se cierra?",
                      primary: "Cerrar",
                      onConfirm: async (note) => {
                        const { error: err } = await supabase.rpc("close_egreso", { p_egreso: id, p_note: note });
                        if (err) return rpcError(err);
                        done();
                      },
                    }),
            },
          ];

  // Money that came back (returned_in) settles the part of a payment no invoice
  // covers — it is not "sin factura", it is no longer spent. Newest payments
  // first: an overpayment is usually the last one.
  const returnedOn = {};
  {
    let left = moneyLines.filter((m) => m.kind === "returned_in").reduce((s, m) => s + Number(m.amount_cents), 0);
    for (const m of [...moneyLines].reverse()) {
      if (left <= 0) break;
      if (m.kind !== "paid_out") continue;
      const part = Math.min(left, Math.max(Number(m.amount_cents) - Number(m.allocated_cents), 0));
      returnedOn[m.entry_id] = part;
      left -= part;
    }
  }

  // Which invoices a payment covered, and which payments paid an invoice.
  const coversOf = (moneyId) => coverage.filter((x) => x.money === moneyId);
  const paidBy = (costId) => coverage.filter((x) => x.cost === costId);

  // Contextual money actions — only those that make sense for this egreso now.
  const moneyActions = canPay && !closed
    ? [
        { key: "paid_out", label: "Registrar pago manual", show: true },
        { key: "returned_in", label: "Nos regresó dinero", show: (bal?.por_comprobar_cents || 0) > 0 && (bal?.cost_cents || 0) > 0 },
        { key: "deposit_in", label: "Nos devolvieron depósito", show: (bal?.deposito_cents || 0) > 0 },
      ].filter((a) => a.show)
    : [];
  const suggestedFor = (k) =>
    ({ paid_out: bal?.por_pagar_cents, returned_in: bal?.por_comprobar_cents, deposit_in: bal?.deposito_cents })[k];

  return (
    <AppWrapper>
      <Page
        title={eg.title}
        titleMetadata={<Badge tone={st.tone}>{st.label}</Badge>}
        subtitle={`${requester ? `Solicitud de ${requester} · ` : ""}Egreso #${eg.id}${eg.category ? ` · ${eg.category}` : ""}`}
        backAction={{ content: "Egresos", url: "/egresos" }}
        primaryAction={primaryAction}
        secondaryActions={closeAction}
      >
        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
              {eg.approval === "rechazada" ? (
                <Banner tone="critical">Rechazada: {eg.reject_reason}</Banner>
              ) : (
                closed && eg.close_note && <Banner>Cerrado: {eg.close_note}</Banner>
              )}
              {createdProblems.length > 0 && (
                <Banner tone="warning" title="El egreso se creó, pero el documento no se adjuntó">
                  {createdProblems.join(" ")}
                </Banner>
              )}

              <Card>
                {allClear ? (
                  <InlineStack gap="200" blockAlign="center">
                    <Badge tone="success">Al corriente</Badge>
                    <Text>{money(bal?.cost_cents)} facturado y pagado.</Text>
                  </InlineStack>
                ) : (
                  <InlineGrid columns={{ xs: 2, md: 4 }} gap="400">
                    <Stat label="Total" value={money(Math.max(bal?.cost_cents || 0, eg.committed_cents || 0))} />
                    <Stat label="Pagado" value={money(bal?.net_paid_cents)} />
                    {bal?.por_pagar_cents > 0 && <Stat label="Por pagar" value={money(bal.por_pagar_cents)} tone="caution" />}
                    {bal?.por_comprobar_cents > 0 && (
                      <Stat
                        label={bal.cost_cents > 0 ? "Nos deben" : "Pagado sin factura"}
                        value={money(bal.por_comprobar_cents)}
                        tone="critical"
                      />
                    )}
                    {bal?.deposito_cents > 0 && <Stat label="Depósito por recuperar" value={money(bal.deposito_cents)} tone="caution" />}
                  </InlineGrid>
                )}
              </Card>

              <PendingSpeiCard request={spei.request} payout={spei.payout} inFlight={spei.inFlight} onCancelled={refresh} />

              {planRows.length > 0 && (
                <Card>
                  <BlockStack gap="200">
                    <Text variant="headingSm">Plan de pagos</Text>
                    {planRows.map((p) => (
                      <InlineStack key={p.id} align="space-between" blockAlign="center" wrap={false}>
                        <InlineStack gap="100" blockAlign="center" wrap={false}>
                          <Text>{p.label}</Text>
                          <Text as="span" tone="subdued">· {TRIGGER_LABEL[p.trigger]}</Text>
                          {!closed && canEdit && p.covered < p.amount_cents ? (
                            <PlannedDate planned={p} onSaved={load} onError={setError} />
                          ) : (
                            p.due_date && <Text as="span" tone="subdued">{fmtDate(p.due_date)}</Text>
                          )}
                        </InlineStack>
                        <InlineStack gap="200" blockAlign="center" wrap={false}>
                          <Text numeric>{money(p.amount_cents)}</Text>
                          {p.covered >= p.amount_cents ? (
                            <Badge tone="success">Pagado</Badge>
                          ) : p.covered > 0 ? (
                            <Badge tone="attention">{`Faltan ${money(p.amount_cents - p.covered)}`}</Badge>
                          ) : (
                            <Badge>Pendiente</Badge>
                          )}
                        </InlineStack>
                      </InlineStack>
                    ))}
                  </BlockStack>
                </Card>
              )}

              <Card padding="0">
                <SectionHeader title="Facturas">
                  {canManage && !closed && !eg.no_invoice_reason && costs.length === 0 && (
                    <Button size="slim" variant="plain" onClick={() => setModal({ type: "noInvoice" })}>No lleva factura</Button>
                  )}
                  {canManage && !closed && !eg.no_invoice_reason && (
                    <Button size="slim" onClick={() => setModal({ type: "cost" })}>Agregar factura</Button>
                  )}
                </SectionHeader>
                {eg.no_invoice_reason ? (
                  <div style={{ padding: "0 16px 16px" }}>
                    <InlineStack align="space-between" blockAlign="center" gap="200">
                      <Text tone="subdued">
                        No lleva factura · {NO_INVOICE_LABEL[eg.no_invoice_reason]}
                        {eg.no_invoice_note ? ` · ${eg.no_invoice_note}` : ""}. El comprobante del pago es el respaldo.
                      </Text>
                      {canManage && !closed && (
                        <Button
                          variant="plain"
                          onClick={async () => {
                            const { error: err } = await supabase.rpc("set_egreso_no_invoice", { p_egreso: id, p_reason: null });
                            if (err) return setError(rpcError(err));
                            refresh();
                          }}
                        >
                          Deshacer
                        </Button>
                      )}
                    </InlineStack>
                  </div>
                ) : costs.length === 0 ? (
                  <Empty>Todavía no hay factura.</Empty>
                ) : (
                  <FixedTable
                    columns={[
                      { title: "Fecha", width: 104 },
                      { title: "Factura" },
                      { title: "Monto", width: 112, align: "right" },
                      { title: "Estado", width: 128 },
                      { title: "", width: 44 },
                    ]}
                    rows={costs.map((c) => {
                      const paid = paidBy(c.entry_id).reduce((s, x) => s + x.cents, 0);
                      const isCredit = c.cost_cents < 0;
                      const problem = c.deductible_status === "no_deducible" && NOT_DEDUCTIBLE_SHOWN.test(c.deductible_reason || "");
                      return {
                        key: c.entry_id,
                        cells: [
                          fmtDate(c.occurred_at),
                          <BlockStack gap="0">
                            <Text truncate fontWeight="semibold">
                              {c.cfdi_uuid
                                ? `${isCredit ? "Nota de crédito" : "Factura"} ${folioOf(c)}`
                                : c.kind === "writeoff"
                                  ? "Pérdida"
                                  : "Ticket (sin factura)"}
                            </Text>
                            <Text truncate tone="subdued" variant="bodySm">
                              {[c.emisor_nombre, c.description].filter(Boolean).join(" · ") || " "}
                            </Text>
                          </BlockStack>,
                          <Text numeric alignment="end">{money(c.cost_cents)}</Text>,
                          problem ? (
                            <Tooltip content={c.deductible_reason}>
                              <Badge tone="critical">No deducible</Badge>
                            </Tooltip>
                          ) : isCredit ? (
                            <Badge tone="info">Aplicada</Badge>
                          ) : paid >= c.cost_cents ? (
                            <Badge tone="success">Pagada</Badge>
                          ) : paid > 0 ? (
                            <Badge tone="attention">Pagada en parte</Badge>
                          ) : (
                            <Badge>Por pagar</Badge>
                          ),
                          <InlineStack gap="0" wrap={false}>
                            {c.ticket_path && (
                              <IconButton icon={ImportIcon} label="Ver ticket" onClick={() => openEgresoDoc(c.ticket_path)} />
                            )}
                            {!closed && canPay && (
                              <IconButton icon={DeleteIcon} label="Quitar (se agregó por error)" onClick={() => remove(c.entry_id, "factura")} />
                            )}
                          </InlineStack>,
                        ],
                      };
                    })}
                  />
                )}
              </Card>

              <Card padding="0">
                <SectionHeader title="Pagos">
                  {moneyActions.map((a) => (
                    <Button key={a.key} size="slim" onClick={() => setModal({ type: "money", kind: a.key, suggested: suggestedFor(a.key) })}>
                      {a.label}
                    </Button>
                  ))}
                </SectionHeader>
                {moneyLines.length === 0 ? (
                  <Empty>Sin pagos todavía.</Empty>
                ) : (
                  <FixedTable
                    columns={[
                      { title: "Fecha", width: 104 },
                      { title: "Pago" },
                      { title: "Factura", width: 168 },
                      { title: "Monto", width: 112, align: "right" },
                      { title: "", width: 72 },
                    ]}
                    rows={moneyLines.map((m) => {
                      const covers = coversOf(m.entry_id);
                      const isOut = m.kind === "paid_out";
                      const unalloc = isOut ? m.amount_cents - m.allocated_cents - (returnedOn[m.entry_id] || 0) : 0;
                      const what = {
                        paid_out: "Pago",
                        returned_in: "Nos regresó",
                        deposit_out: "Depósito entregado",
                        deposit_in: "Depósito devuelto",
                      }[m.kind];
                      return {
                        key: m.entry_id,
                        cells: [
                          fmtDate(m.occurred_at),
                          <BlockStack gap="0">
                            <Text truncate fontWeight="semibold">{cleanConcept(m.description) || what}</Text>
                            <Text truncate tone="subdued" variant="bodySm">
                              {[m.method === "spei" ? "SPEI" : METHOD_LABEL[m.method], isOut ? null : cleanConcept(m.description) ? what : null]
                                .filter(Boolean)
                                .join(" · ")}
                            </Text>
                          </BlockStack>,
                          isOut ? (
                            <BlockStack gap="0">
                              {covers.map((x) => {
                                const c = costById[x.cost];
                                const label = c?.cfdi_uuid ? folioOf(c) : "Ticket";
                                // The amount only when the payment is split across invoices.
                                return (
                                  <Text key={x.cost} truncate variant="bodySm">
                                    {covers.length > 1 || unalloc > 0 ? `${label} · ${money(x.cents)}` : label}
                                  </Text>
                                );
                              })}
                              {unalloc > 0 && !eg.no_invoice_reason && (
                                <Badge tone="warning">{covers.length ? `Sin factura ${money(unalloc)}` : "Sin factura"}</Badge>
                              )}
                            </BlockStack>
                          ) : (
                            <Text tone="subdued">—</Text>
                          ),
                          <Text numeric alignment="end">
                            {m.kind === "returned_in" || m.kind === "deposit_in" ? `−${money(m.amount_cents)}` : money(m.amount_cents)}
                          </Text>,
                          <InlineStack gap="0" wrap={false} align="end">
                            {m.payout_id && speiEnabled() ? (
                              <IconButton icon={ImportIcon} label="Comprobante SPEI" onClick={() => openSpeiReceipt(m.payout_id).catch((e) => setError(e.message))} />
                            ) : m.receipt_path ? (
                              <IconButton icon={ImportIcon} label="Comprobante" onClick={() => openEgresoDoc(m.receipt_path)} />
                            ) : null}
                            {!closed && canPay && !m.payout_id && (
                              <IconButton icon={DeleteIcon} label="Quitar (se registró por error)" onClick={() => remove(m.entry_id, "pago")} />
                            )}
                          </InlineStack>,
                        ],
                      };
                    })}
                  />
                )}
              </Card>

              <RepCard coverage={coverage} costs={costs} costById={costById} moneyById={moneyById} repDocs={repDocs} payeeName={payee?.payee_nickname || payee?.payee_name} onSaved={load} closed={closed} />
            </BlockStack>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <Card>
                <BlockStack gap="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingSm">{PAYEE_TYPE_LABEL[eg.payee_type]}</Text>
                    {canManage && eg.payee_type === "payee" && (
                      <Button variant="plain" onClick={() => setModal({ type: "editPayee" })}>Editar</Button>
                    )}
                  </InlineStack>
                  {payeeUrl(eg.payee_type, eg.payee_id) ? (
                    <Link url={payeeUrl(eg.payee_type, eg.payee_id)}>{payee?.payee_nickname || payee?.payee_name}</Link>
                  ) : (
                    <Text fontWeight="semibold">{payee?.payee_nickname || payee?.payee_name}</Text>
                  )}
                  {payee?.payee_nickname && <Text tone="subdued">{payee.payee_name}</Text>}
                  {payee?.payee_rfc && <Text tone="subdued">RFC {payee.payee_rfc}</Text>}
                  {client && (
                    <Text>
                      Para: <Link url={client.url}>{client.name}</Link>
                    </Text>
                  )}
                </BlockStack>
              </Card>
              <AccountCard account={account} canPay={canPay} onChanged={reloadAccount} />
              <ReturnClabeCard egreso={eg} balance={bal} payeeName={payee?.payee_name} canPay={canPay} onApplied={refresh} />
              <CarCard egreso={eg} editable={!closed && canEdit} onSaved={load} onError={setError} />
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingSm">Datos</Text>
                  {!closed && canEdit && <EgresoCategory egreso={eg} onSaved={load} onError={setError} />}
                  <Text tone="subdued">Creado {fmtDate(eg.created_at)}</Text>
                  {!closed && canEdit ? (
                    <InlineStack gap="100" blockAlign="center">
                      <Text tone="subdued">Fecha de pago</Text>
                      <DateLink
                        value={eg.due_date}
                        onSave={(d) => supabase.rpc("update_egreso", { p_id: eg.id, p: { due_date: d } })}
                        onClear={() => supabase.rpc("update_egreso", { p_id: eg.id, p: { due_date: null } })}
                        onSaved={load}
                        onError={setError}
                      />
                    </InlineStack>
                  ) : (
                    eg.due_date && <Text tone="subdued">Fecha de pago {fmtDate(eg.due_date)}</Text>
                  )}
                  {eg.reservation_id && <Link url={`/reservation?id=${eg.reservation_id}`}>Reservación #{eg.reservation_id}</Link>}
                  {eg.quote_path && <Link onClick={() => openEgresoDoc(eg.quote_path)}>Ver cotización</Link>}
                  {eg.notes && <Text>{eg.notes}</Text>}
                </BlockStack>
              </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>

      {modal?.type === "money" && <MoneyModal egreso={eg} kind={modal.kind} suggestedCents={modal.suggested} onClose={() => setModal(null)} onDone={done} />}
      {modal?.type === "cost" && (
        <CostModal
          egreso={eg}
          payeeRfc={payee?.payee_rfc}
          targets={[
            bal?.por_comprobar_cents,
            bal?.por_pagar_cents,
            (eg.committed_cents || 0) - (bal?.cost_cents || 0),
            ...planRows.map((p) => p.amount_cents),
          ]
            .map(Number)
            .filter((c) => c > 0)}
          onClose={() => setModal(null)}
          onDone={done}
        />
      )}
      {modal?.type === "reason" && <ReasonModal {...modal} onClose={() => setModal(null)} />}
      {modal?.type === "noInvoice" && <NoInvoiceModal egresoId={id} onClose={() => setModal(null)} onDone={done} />}
      {modal?.type === "editPayee" && <EditPayeeModal payeeId={eg.payee_id} onClose={() => setModal(null)} onSaved={done} />}
      {modal?.type === "speiRequest" && (
        <SpeiRequestModal
          egreso={eg}
          balance={bal}
          nextPlanned={nextPlanned}
          onClose={() => setModal(null)}
          onCreated={(requestId) => {
            spei.reload();
            setModal({ type: "send", requestId });
          }}
        />
      )}
      {modal?.type === "send" && (
        <SpeiPaymentModal
          open
          payableKind="egreso"
          payableId={modal.requestId}
          nounLabel="egreso"
          beneficiaryLabel="beneficiario"
          onClose={done}
          onAuthorized={refresh}
        />
      )}
    </AppWrapper>
  );
}

// The car this egreso belongs to: a real fleet car, settable and changeable
// while the egreso is open.
function CarCard({ egreso, editable, onSaved, onError }) {
  const [editing, setEditing] = useState(false);
  const save = async (carId) => {
    const { error } = await supabase.rpc("update_egreso", { p_id: egreso.id, p: { car_id: carId } });
    if (error) return onError(rpcError(error));
    setEditing(false);
    onSaved();
  };
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm">Auto</Text>
          {editable && !editing && (
            <Button variant="plain" onClick={() => setEditing(true)}>{egreso.car ? "Cambiar" : "Asignar"}</Button>
          )}
        </InlineStack>
        {editing ? (
          <BlockStack gap="200">
            <CarPicker label="Buscar auto" autoFocus onPick={(c) => save(c.id)} />
            <InlineStack gap="200">
              {egreso.car && <Button variant="plain" tone="critical" onClick={() => save(null)}>Quitar auto</Button>}
              <Button variant="plain" onClick={() => setEditing(false)}>Cancelar</Button>
            </InlineStack>
          </BlockStack>
        ) : egreso.car ? (
          <Link url={`/cars/${egreso.car.id}`}>
            {formatPlate(egreso.car.plate)} · {egreso.car.model?.name} {egreso.car.year}
          </Link>
        ) : egreso.sublease_plate ? (
          <Text>{egreso.sublease_plate} (subarrendado)</Text>
        ) : (
          <Text tone="subdued">Sin auto</Text>
        )}
      </BlockStack>
    </Card>
  );
}

// The date of an unpaid planned payment, changeable in place. Setting one
// makes the line "En fecha" (set_egreso_planned_date).
function PlannedDate({ planned, onSaved, onError }) {
  return (
    <DateLink
      value={planned.due_date}
      onSave={(d) => supabase.rpc("set_egreso_planned_date", { p_planned: planned.id, p_due: d })}
      onSaved={onSaved}
      onError={onError}
    />
  );
}

// A date shown as a link that opens a picker. `onSave(date)` returns the
// Supabase call; `onClear` (optional) offers "Quitar".
function DateLink({ value, onSave, onClear, onSaved, onError }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const run = async (call) => {
    setSaving(true);
    const { error } = await call();
    setSaving(false);
    if (error) return onError(rpcError(error));
    setOpen(false);
    onSaved();
  };
  return (
    <Popover
      active={open}
      onClose={() => setOpen(false)}
      activator={
        <Button variant="plain" onClick={() => { setDraft(value || ""); setOpen((o) => !o); }}>
          {value ? fmtDate(value) : "Poner fecha"}
        </Button>
      }
    >
      <Box padding="300">
        <BlockStack gap="200">
          <TextField label="Fecha de pago" type="date" value={draft} onChange={setDraft} autoComplete="off" />
          <InlineStack align="end" gap="200">
            {onClear && value && (
              <Button variant="plain" tone="critical" onClick={() => run(onClear)} disabled={saving}>Quitar</Button>
            )}
            <Button variant="primary" size="slim" onClick={() => run(() => onSave(draft))} loading={saving} disabled={!draft || draft === value}>
              Guardar
            </Button>
          </InlineStack>
        </BlockStack>
      </Box>
    </Popover>
  );
}

// Category of an open egreso, changeable in place.
function EgresoCategory({ egreso, onSaved, onError }) {
  const [categories, reload] = useEgresoCategories();
  const save = async (category) => {
    const { error } = await supabase.rpc("update_egreso", { p_id: egreso.id, p: { category } });
    if (error) return onError(rpcError(error));
    onSaved();
  };
  return <CategorySelect value={egreso.category} onChange={save} categories={categories} onAdded={reload} />;
}

function Stat({ label, value, tone }) {
  return (
    <BlockStack gap="050">
      <Text tone="subdued" variant="bodySm">{label}</Text>
      <Text variant="headingLg" tone={tone}>{value}</Text>
    </BlockStack>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div style={{ padding: "16px 16px 8px" }}>
      <InlineStack align="space-between" blockAlign="center" gap="200">
        <Text variant="headingSm">{title}</Text>
        <InlineStack gap="200">{children}</InlineStack>
      </InlineStack>
    </div>
  );
}

function Empty({ children }) {
  return (
    <div style={{ padding: "0 16px 16px" }}>
      <Text tone="subdued">{children}</Text>
    </div>
  );
}

// A plain table with fixed column widths: never scrolls sideways; long text
// truncates instead of widening the page.
function FixedTable({ columns, rows }) {
  const cell = (c) => ({
    padding: "10px 12px",
    textAlign: c.align || "left",
    verticalAlign: "middle",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  });
  return (
    <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
      <colgroup>
        {columns.map((c, i) => (
          <col key={i} style={c.width ? { width: c.width } : undefined} />
        ))}
      </colgroup>
      <thead>
        <tr style={{ background: "var(--p-color-bg-surface-secondary)" }}>
          {columns.map((c, i) => (
            <th key={i} style={{ ...cell(c), paddingTop: 8, paddingBottom: 8 }}>
              <Text variant="bodySm" tone="subdued" fontWeight="medium">{c.title}</Text>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} style={{ borderTop: "1px solid var(--p-color-border-secondary)" }}>
            {r.cells.map((content, i) => (
              <td key={i} style={cell(columns[i])}>
                {typeof content === "string" ? <Text truncate>{content}</Text> : content}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IconButton({ icon, label, onClick }) {
  return (
    <Tooltip content={label}>
      <Button variant="tertiary" icon={icon} accessibilityLabel={label} onClick={onClick} />
    </Tooltip>
  );
}

// PPD invoices: each payment applied to them needs its complemento de pago
// (REP). Shown only when one is missing.
// Complementos de pago (REP) of the PPD invoices. They arrive from SAT on
// their own (expense_rep_docs, matched to each payment by match_expense_reps);
// the box is only for a payment whose REP never came. When the provider's
// own remaining balance (latest REP) differs from ours, say so: that is a
// payment they have not applied, or applied elsewhere.
function RepCard({ coverage, costs, costById, moneyById, repDocs, payeeName, onSaved, closed }) {
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  const ppd = coverage.filter((x) => costById[x.cost]?.metodo_pago === "PPD" && x.id);
  const missing = ppd.filter((x) => !x.rep);
  const unmatched = repDocs.filter((d) => !d.allocation_id);
  const diffs = costs
    .filter((c) => c.metodo_pago === "PPD" && c.cfdi_uuid)
    .map((c) => {
      const docs = repDocs.filter((d) => d.invoice_uuid === c.cfdi_uuid.toUpperCase());
      const last = docs[docs.length - 1];
      if (!last || last.imp_saldo_insoluto == null) return null;
      const theirs = Math.round(Number(last.imp_saldo_insoluto) * 100);
      const ours = Math.round(Number(c.unpaid_cents));
      return Math.abs(theirs - ours) > 100 ? { c, theirs, ours } : null;
    })
    .filter(Boolean);
  if (!ppd.length && !repDocs.length) return null;
  const save = async (x) => {
    const { error: err } = await supabase.rpc("set_egreso_allocation_rep", { p_allocation: x.id, p_rep_uuid: values[x.id] });
    if (err) return setError(rpcError(err));
    onSaved();
  };
  const who = payeeName || "El proveedor";
  const allGood = !missing.length && !diffs.length && !unmatched.length;
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm">Complementos de pago</Text>
          {allGood ? <Badge tone="success">Completos</Badge> : missing.length > 0 && <Badge tone="warning">{`Faltan ${missing.length}`}</Badge>}
        </InlineStack>
        {error && <Banner tone="critical">{error}</Banner>}
        {diffs.map(({ c, theirs, ours }) => (
          <Banner key={c.entry_id} tone="critical">
            {`${who} dice que de la factura ${folioOf(c)} faltan ${money(theirs)}; aquí faltan ${money(ours)}.`}
          </Banner>
        ))}
        {unmatched.map((d) => (
          <Text key={d.id} tone="critical">
            {`REP del ${fmtDate(d.fecha_pago)} por ${money(Math.round(Number(d.imp_pagado) * 100))} a la factura ${d.serie_folio || d.invoice_uuid.slice(0, 8)}: no coincide con ningún pago registrado.`}
          </Text>
        ))}
        {!closed &&
          missing.map((x) => {
            const c = costById[x.cost];
            const m = moneyById[x.money];
            return (
              <InlineStack key={x.id} gap="200" blockAlign="center" wrap={false}>
                <Text>
                  Pago {fmtDate(m?.occurred_at)} {money(x.cents)} → Factura {folioOf(c)}
                </Text>
                <div style={{ flex: 1 }}>
                  <input
                    style={{ width: "100%", padding: 6, borderRadius: 6, border: "1px solid #ccc" }}
                    placeholder="UUID del REP"
                    value={values[x.id] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [x.id]: e.target.value }))}
                  />
                </div>
                <Button size="slim" disabled={!values[x.id]} onClick={() => save(x)}>Guardar</Button>
              </InlineStack>
            );
          })}
      </BlockStack>
    </Card>
  );
}
